<template>
    <div>
        <div class="flex items-start gap-2 border-b border-gray-100 px-4 py-3">
            <div class="min-w-0 flex-1">
                <p class="text-sm font-medium break-words text-gray-900">{{ turn.question }}</p>

                <!-- How the question was read. Shown quietly, because a person
                     needs to be able to tell a misread question from a
                     surprising answer — and in a thread it is also where a
                     filter carried over from an earlier turn becomes visible. -->
                <p v-if="turn.plan?.reasoning_note" class="mt-1 text-xs text-gray-500">
                    {{ turn.plan.reasoning_note }}
                </p>
            </div>
        </div>

        <div v-if="working" class="flex items-center gap-3 px-4 py-8 text-sm text-gray-500">
            <Loader class="h-4 w-4" />
            Working through your data…
        </div>

        <div v-else-if="turn.status === 'failed'" class="px-4 py-6 text-sm text-red-700">
            {{ turn.error || 'That question could not be answered.' }}
        </div>

        <!-- Clarify is a real outcome, not an error state. -->
        <div v-else-if="turn.clarification" class="px-4 py-5">
            <p class="text-sm text-gray-800">{{ turn.clarification }}</p>
            <p class="mt-2 text-xs text-gray-500">Reply below with a bit more detail and I'll try again.</p>
        </div>

        <div v-else class="px-4 py-4">
            <!-- The chips. The single most important trust affordance here: the
                 person must be able to see WHAT was asked, not only what came
                 back. -->
            <div v-if="chips.length" class="mb-3 flex flex-wrap items-center gap-1.5">
                <span v-for="(chip, i) in chips" :key="i" class="rounded-md bg-gray-100 px-2 py-1 font-mono text-xs text-gray-700">
                    {{ chip }}
                </span>
            </div>

            <p v-if="turn.narration" class="mb-3 text-sm leading-relaxed text-gray-800">
                {{ turn.narration }}
            </p>

            <!-- Measure -->
            <div v-if="turn.surface === 'measure'" class="space-y-3">
                <div>
                    <p :class="compact ? 'text-2xl' : 'text-3xl'" class="font-semibold text-gray-900">
                        {{ ask.formatValue(result.value, result.unit) }}
                    </p>
                    <p class="mt-0.5 text-xs text-gray-500">
                        {{ result.label }} · {{ result.window.from }} to {{ result.window.to }}
                        <span v-if="result.provisional" class="ml-1 text-amber-700">· provisional, the daily rollup has not caught up</span>
                    </p>
                </div>

                <p v-if="result.compare" class="text-sm text-gray-700">
                    Previous period: {{ ask.formatValue(result.compare.value, result.unit) }}
                    <span :class="result.compare.change >= 0 ? 'text-green-700' : 'text-red-700'">
                        ({{ result.compare.change >= 0 ? '+' : '' }}{{ ask.formatValue(result.compare.change, result.unit)
                        }}<template v-if="result.compare.change_percent !== null">, {{ result.compare.change_percent }}%</template>)
                    </span>
                </p>

                <div v-if="result.rows?.length" class="overflow-x-auto">
                    <table class="min-w-full text-sm">
                        <tbody>
                            <tr v-for="(row, i) in result.rows" :key="i" class="border-t border-gray-100">
                                <td class="py-1.5 pr-6 text-gray-700">{{ ask.rowLabel(row) }}</td>
                                <td class="py-1.5 text-right font-medium text-gray-900 tabular-nums">
                                    {{ ask.formatValue(row.aggregate, result.unit) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <p v-if="result.note" class="text-xs text-gray-500">{{ result.note }}</p>
            </div>

            <!-- List -->
            <div v-else-if="turn.surface === 'list'">
                <div v-if="!result.rows?.length" class="text-sm text-gray-600">Nothing matched that.</div>

                <div v-else class="overflow-x-auto">
                    <table class="min-w-full text-sm">
                        <thead>
                            <tr class="border-b border-gray-200">
                                <th v-for="col in visibleColumns" :key="col" class="py-2 pr-6 text-left font-medium whitespace-nowrap text-gray-600">
                                    {{ ask.humanise(col) }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, i) in visibleRows" :key="i" class="border-b border-gray-100">
                                <td v-for="col in visibleColumns" :key="col" class="py-1.5 pr-6 whitespace-nowrap text-gray-800">
                                    {{ ask.cell(row, col) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="mt-3 flex flex-wrap items-center gap-4">
                    <p class="text-xs text-gray-500">
                        Showing {{ visibleRows.length }}<template v-if="result.total"> of {{ result.total }}</template>
                    </p>
                    <!-- The answer is a starting point, not a dead end. -->
                    <router-link
                        v-if="result.route"
                        :to="result.route"
                        class="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                        @click="emit('navigated')"
                    >
                        Open as a table →
                    </router-link>
                </div>
            </div>

            <!-- Local — this browser, read here.
                 The server planned where to look and never looked: no value
                 from the caches, the app state, local storage or the cookie jar
                 has been sent anywhere, which is also why there is no narration
                 paragraph above. What is shown is what this device holds now. -->
            <div v-else-if="turn.surface === 'local'">
                <div v-if="turn.localBusy" class="flex items-center gap-3 py-2 text-sm text-gray-500">
                    <Loader class="h-4 w-4" />
                    Reading this browser…
                </div>

                <div v-else-if="turn.localError" class="text-sm text-red-700">{{ turn.localError }}</div>

                <template v-else>
                    <div v-if="!localEntries.length" class="text-sm text-gray-600">Nothing matched that on this device.</div>

                    <ul v-else class="divide-y divide-gray-100">
                        <li v-for="(entry, i) in localEntries" :key="i" class="py-2">
                            <div class="flex items-baseline justify-between gap-3">
                                <span class="min-w-0 truncate text-sm font-medium text-gray-900">{{ entry.name }}</span>
                                <span class="shrink-0 text-[11px] whitespace-nowrap text-gray-400">
                                    {{ ask.localStoreLabel(entry.store) }}
                                    <template v-if="entry.collection"> · {{ entry.collection }}</template>
                                    <template v-if="entry.size !== null"> · {{ formatBytes(entry.size) }}</template>
                                </span>
                            </div>
                            <dl class="mt-1 space-y-0.5">
                                <div v-for="(value, key) in entry.detail" :key="key" class="flex gap-2 text-xs">
                                    <dt class="shrink-0 font-mono text-gray-500">{{ key }}</dt>
                                    <dd class="min-w-0 flex-1 truncate font-mono text-gray-700">{{ value }}</dd>
                                </div>
                            </dl>
                        </li>
                    </ul>

                    <p class="mt-3 text-xs text-gray-500">
                        Showing {{ localEntries.length }}<template v-if="turn.local?.truncated"> of more</template> · read on this device and sent
                        nowhere
                    </p>

                    <p v-for="(note, i) in turn.local?.notes ?? []" :key="'note-' + i" class="mt-1 text-xs text-gray-500">{{ note }}</p>
                </template>
            </div>

            <!-- Report -->
            <div v-else-if="turn.surface === 'report'">
                <p class="text-sm text-gray-800">{{ result.label }} · {{ result.window.from }} to {{ result.window.to }}</p>
                <router-link
                    v-if="result.route"
                    :to="result.route"
                    class="mt-2 inline-block text-xs font-medium text-indigo-600 hover:text-indigo-700"
                    @click="emit('navigated')"
                >
                    Open the full report →
                </router-link>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import Loader from '@/components/Loader.vue';

const props = defineProps({
    /**
     * The whole useAsk() instance. Passed as one object rather than a dozen
     * props because this component is a view onto one conversation and threading
     * each field through separately would only invite the two callers to pass
     * slightly different sets.
     */
    ask: { type: Object, required: true },
    /**
     * The one turn this renders. A conversation shows several at once, so the
     * turn is a prop rather than something read off the instance — an answer
     * halfway up a thread is not "the current answer" and must not redraw when
     * a later question is asked.
     */
    turn: { type: Object, required: true },
    /** The floating launcher is narrow; the page is not. */
    compact: { type: Boolean, default: false },
});

const emit = defineEmits(['navigated']);

const result = computed(() => props.ask.resultOf(props.turn));
const chips = computed(() => props.ask.chipsFor(props.turn));
const working = computed(() => !['completed', 'failed'].includes(props.turn.status));

// A messenger panel four hundred pixels wide cannot show eight columns or
// twenty-five rows legibly. Truncating here rather than asking for less keeps
// the two surfaces answering the identical question, so "open as a table"
// always lands on the full result.
const visibleColumns = computed(() => {
    const columns = result.value.columns ?? [];
    return props.compact ? columns.slice(0, 3) : columns;
});

const visibleRows = computed(() => {
    const rows = result.value.rows ?? [];
    return props.compact ? rows.slice(0, 8) : rows;
});

// Truncated for the narrow panel like every other surface, and for the same
// reason — the two must be answering the identical question, so the cut is here
// rather than in the plan the server validated.
const localEntries = computed(() => {
    const entries = props.turn.local?.entries ?? [];
    return props.compact ? entries.slice(0, 6) : entries;
});

const formatBytes = (bytes) => {
    const n = Number(bytes ?? 0);
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
</script>
